import React from "react";
import { NIGHT_WINDOWS, computeWeek, formatDecimal, formatHM, grossPay } from "./heuresRules";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
// Pre-filled 35 h week (5 × 9 h–17 h, 1 h pause) so the page shows a result
// straight away; weekends empty.
const DEFAULT_DAYS = DAYS.map((_, i) => (i < 5 ? { start: "09:00", end: "17:00", pause: "60" } : { start: "", end: "", pause: "" }));
const EMPTY_DAYS = DAYS.map(() => ({ start: "", end: "", pause: "" }));

const fieldCls = "dropdown m-0 w-full min-w-0 px-1 text-center";
const euros = (n) => n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

function Row({ label, value, sub }) {
    return (
        <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
            <div className="font-semibold text-sm sm:text-base text-gray-900">{label}</div>
            <div className="text-right shrink-0 ml-2">
                <div className="font-bold text-base sm:text-lg text-emerald-800">{value}</div>
                {sub && <div className="text-xs text-gray-600">{sub}</div>}
            </div>
        </div>
    );
}

export default function HeuresTravail() {
    const [days, setDays] = React.useState(DEFAULT_DAYS);
    const [nightWindow, setNightWindow] = React.useState("21-6");
    const [rate, setRate] = React.useState("");

    const week = computeWeek(days, { windowKey: nightWindow });
    const pay = grossPay(week, parseFloat(String(rate).replace(",", ".")));
    const setDay = (i, field, v) => setDays((prev) => prev.map((d, j) => (j === i ? { ...d, [field]: v } : d)));

    return (
        <>
            <div className="flex flex-col gap-2">
                {DAYS.map((name, i) => {
                    const r = week.days[i];
                    return (
                        <div key={name} className="p-3 bg-stone-100 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-sm sm:text-base text-gray-900">{name}</span>
                                <span className="text-sm font-bold text-gray-900">
                                    {r && !r.error ? <>{formatHM(r.worked)}{r.overnight && <span className="font-normal text-gray-600"> (nuit)</span>}</> : "—"}
                                </span>
                            </div>
                            <div className="grid grid-cols-[1fr_1fr_4.5rem] items-end gap-1.5 sm:gap-3">
                                <label className="flex flex-col min-w-0 text-xs text-gray-600">Début
                                    <input type="time" className={fieldCls} value={days[i].start} onChange={(e) => setDay(i, "start", e.target.value)} aria-label={`${name} : heure de début`} />
                                </label>
                                <label className="flex flex-col min-w-0 text-xs text-gray-600">Fin
                                    <input type="time" className={fieldCls} value={days[i].end} onChange={(e) => setDay(i, "end", e.target.value)} aria-label={`${name} : heure de fin`} />
                                </label>
                                <label className="flex flex-col min-w-0 text-xs text-gray-600">Pause (min)
                                    <input type="text" inputMode="numeric" className={fieldCls} value={days[i].pause} placeholder="0"
                                        onChange={(e) => setDay(i, "pause", e.target.value.replace(/[^0-9]/g, ""))} aria-label={`${name} : pause en minutes`} />
                                </label>
                            </div>
                            {r && r.error && <p className="text-xs text-red-700 mt-1">{r.error}</p>}
                        </div>
                    );
                })}
            </div>

            <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg flex flex-col gap-3 text-sm">
                <label className="flex flex-col gap-1">
                    <span className="font-semibold text-gray-900">Plage des heures de nuit</span>
                    <select className="dropdown-filter select" value={nightWindow} onChange={(e) => setNightWindow(e.target.value)}>
                        {Object.entries(NIGHT_WINDOWS).map(([k, w]) => <option key={k} value={k}>{w.label}</option>)}
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    <span className="font-semibold text-gray-900">Taux horaire brut <span className="font-normal text-gray-500">(optionnel)</span></span>
                    <span className="flex items-center gap-2">
                        <input type="text" inputMode="decimal" className="dropdown m-0 w-24 text-center" value={rate} placeholder="—"
                            onChange={(e) => setRate(e.target.value.replace(/[^0-9.,]/g, ""))} />
                        <span className="font-bold">€ / h</span>
                    </span>
                </label>
                <button type="button" onClick={() => setDays(EMPTY_DAYS)} className="self-start underline text-gray-700 hover:text-gray-900">Vider le tableau</button>
            </div>

            {week.daysWorked === 0 ? (
                <div className="p-6 my-2 sm:my-4 bg-stone-50 border border-dashed border-stone-300 rounded-lg text-center">
                    <p className="text-gray-900 font-semibold text-sm sm:text-base">Renseignez vos horaires pour calculer vos heures</p>
                    <p className="text-gray-600 text-sm mt-2">Une heure de fin antérieure à l'heure de début est comptée sur le lendemain (service de nuit).</p>
                </div>
            ) : (
                <>
                    <div className="p-4 my-2 sm:my-4 rounded-lg w-full bg-gradient-to-b from-emerald-900 to-emerald-700 text-white text-center">
                        <div className="text-sm text-emerald-200">Heures travaillées sur la semaine</div>
                        <div className="text-4xl sm:text-5xl font-bold my-1">{formatHM(week.total)}</div>
                        <div className="text-sm">soit <strong>{formatDecimal(week.total)} h</strong> en centièmes · {week.daysWorked} jour{week.daysWorked > 1 ? "s" : ""} travaillé{week.daysWorked > 1 ? "s" : ""}</div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Row label="Équivalent mensuel" value={`${formatDecimal(week.monthly)} h`} sub="heures hebdo × 52 ÷ 12" />
                        <Row label={`Heures de nuit (${NIGHT_WINDOWS[nightWindow].label})`} value={formatHM(week.night)} sub="pauses non déduites" />
                        <Row label={"Heures sup. à 25\u00a0%"} value={formatHM(week.hs25)} sub="de la 36e à la 43e heure" />
                        <Row label={"Heures sup. à 50\u00a0%"} value={formatHM(week.hs50)} sub="à partir de la 44e heure" />
                        {pay !== null && <Row label="Salaire brut estimé" value={euros(pay)} sub="majorations légales par défaut" />}
                    </div>

                    {(week.alerts.longDays.length > 0 || week.alerts.shortPause.length > 0 || week.alerts.overWeek) && (
                        <div className="p-3 my-2 sm:my-4 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-900">
                            <p className="font-semibold mb-1">Points de vigilance</p>
                            <ul className="list-disc pl-4">
                                {week.alerts.longDays.length > 0 && <li>Plus de 10 h de travail le {week.alerts.longDays.map((i) => DAYS[i].toLowerCase()).join(", ")} : maximum légal, sauf dérogation (jusqu'à 12 h, notamment dans les établissements de santé).</li>}
                                {week.alerts.shortPause.length > 0 && <li>Moins de 20 min de pause le {week.alerts.shortPause.map((i) => DAYS[i].toLowerCase()).join(", ")} : une pause de 20 min est due dès 6 h de travail.</li>}
                                {week.alerts.overWeek && <li>Plus de 48 h sur la semaine : c'est la durée maximale hebdomadaire.</li>}
                            </ul>
                        </div>
                    )}
                    <p className="text-xs text-gray-600 mt-2">Heures supplémentaires calculées au-delà de 35 h avec les taux légaux par défaut : un accord collectif peut prévoir d'autres taux (10 % minimum). À temps partiel, les heures en plus sont des heures complémentaires.</p>
                </>
            )}
        </>
    );
}
